import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { ArrowLeft, Check, KeyRound, LogOut, Pencil, Minus, Plus, X } from 'lucide-react';
import { api, ErroApi } from '../../api';
import { NOME_PAPEL, pode, useSessao } from '../../sessao';
import { Modal } from '../../componentes/Modal';
import { PAPEIS_EDITAVEIS, SenhaProvisoria, placaFmt, type Veiculo } from './Equipe';

interface Perm { codigo: string; grupo: string; descricao: string; padrao: boolean; efetiva: boolean; ajuste: { concedida: boolean; motivo: string | null; criado_em: string; criado_por: string | null } | null }
interface Detalhe {
  id: string; nome: string; email: string; telefone: string | null; papel: string; ativo: boolean; trocar_senha: boolean; ultimo_login_em: string | null;
  criado_em: string; versao: number; veiculo_id: string | null; especialidades: string[] | null; permissoes: Perm[];
  sessoes: { id: string; dispositivo: string | null; criado_em: string; ultimo_uso_em: string | null }[];
}
type Escolha = 'padrao' | 'conceder' | 'retirar';
const quando = (iso: string | null) => iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';
const telefoneFmt = (t: string | null) => t ? t.replace(/^55(\d\d)(\d{4,5})(\d{4})$/, '($1) $2-$3') : '—';

function FormDados({ d, aoSalvar, aoCancelar, proprio }: { d: Detalhe; aoSalvar: () => void; aoCancelar: () => void; proprio: boolean }) {
  const [f, setF] = useState({ nome: d.nome, telefone: d.telefone ? telefoneFmt(d.telefone) : '', papel: d.papel });
  const [erro, setErro] = useState<string | null>(null);
  async function enviar(e: FormEvent) {
    e.preventDefault(); setErro(null);
    try { await api(`/usuarios/${d.id}`, { metodo: 'PATCH', corpo: { nome: f.nome, telefone: f.telefone || null, ...(f.papel !== d.papel ? { papel: f.papel } : {}), versao: d.versao } }); aoSalvar(); }
    catch (err) { setErro(err instanceof Error ? err.message : 'Não foi possível salvar.'); }
  }
  return (
    <form className="form" onSubmit={enviar}>
      <label>Nome<input required minLength={2} maxLength={120} value={f.nome} onChange={(e) => setF({ ...f, nome: e.target.value })} /></label>
      <div className="linha-2">
        <label>Papel
          <select value={f.papel} onChange={(e) => setF({ ...f, papel: e.target.value })} disabled={proprio} title={proprio ? 'Ninguém muda o próprio papel' : undefined}>
            {PAPEIS_EDITAVEIS.map((p) => <option key={p} value={p}>{NOME_PAPEL[p]}</option>)}
          </select>
        </label>
        <label>Telefone<input inputMode="tel" maxLength={30} value={f.telefone} onChange={(e) => setF({ ...f, telefone: e.target.value })} /></label>
      </div>
      {f.papel !== d.papel && <p className="alerta alerta-aviso">Mudar o papel troca as permissões padrão. As exceções desta pessoa continuam valendo — confira a lista depois.</p>}
      <small className="ajuda">O e-mail é o login e não muda aqui.</small>
      {erro && <p className="alerta" role="alert">{erro}</p>}
      <div className="acoes-form">
        <button type="button" className="btn-fantasma" onClick={aoCancelar}>Cancelar</button>
        <button className="btn">Salvar</button>
      </div>
    </form>
  );
}

/** Permissões: cada linha mostra o padrão do papel e deixa conceder ou retirar só para esta pessoa (com motivo). */
function Permissoes({ d, podeGerir, proprio, aoSalvar }: { d: Detalhe; podeGerir: boolean; proprio: boolean; aoSalvar: () => void }) {
  const inicial = useMemo(() => Object.fromEntries(d.permissoes.map((p) => [p.codigo, {
    escolha: (p.ajuste ? (p.ajuste.concedida ? 'conceder' : 'retirar') : 'padrao') as Escolha, motivo: p.ajuste?.motivo ?? '' }])), [d]);
  const [m, setM] = useState(inicial);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const [salvando, setSalvando] = useState(false);
  useEffect(() => setM(inicial), [inicial]);
  const mudou = JSON.stringify(m) !== JSON.stringify(inicial);
  const grupos = [...new Set(d.permissoes.map((p) => p.grupo))];

  async function salvar() {
    setErro(null); setOk(false); setSalvando(true);
    const ajustes = Object.entries(m).filter(([, v]) => v.escolha !== 'padrao').map(([permissao, v]) => ({ permissao, concedida: v.escolha === 'conceder', motivo: v.motivo.trim() || null }));
    try { await api(`/usuarios/${d.id}/permissoes`, { metodo: 'PUT', corpo: { ajustes } }); setOk(true); aoSalvar(); }
    catch (err) { setErro(err instanceof Error ? err.message : 'Não foi possível salvar.'); } finally { setSalvando(false); }
  }

  return (
    <section className="secao">
      <div className="secao-topo">
        <h2>Permissões</h2>
        {podeGerir && mudou && (
          <div className="botoes">
            <button className="btn-fantasma" onClick={() => setM(inicial)}>Desfazer</button>
            <button className="btn" disabled={salvando} onClick={salvar}>{salvando ? 'Salvando…' : 'Salvar permissões'}</button>
          </div>
        )}
      </div>
      <p className="nota">O papel <strong>{NOME_PAPEL[d.papel]}</strong> traz o padrão. Dar ou tirar uma permissão aqui vale só para {d.nome.split(' ')[0]} e fica registrado com quem fez e por quê. Mudanças valem na próxima ação da pessoa — não precisa sair e entrar.</p>
      {erro && <p className="alerta" role="alert">{erro}</p>}
      {ok && !mudou && <p className="alerta alerta-ok" role="status">Permissões salvas.</p>}
      <div className="cartao tabela-cartao rolagem-x">
        <table className="tabela tabela-permissoes">
          <thead><tr><th>Permissão</th><th>Padrão do papel</th><th>Para esta pessoa</th><th>Fica</th></tr></thead>
          {grupos.map((g) => (
            <tbody key={g}>
              <tr className="grupo"><th colSpan={4}>{g}</th></tr>
              {d.permissoes.filter((p) => p.grupo === g).map((p) => {
                const v = m[p.codigo]!;
                const fica = v.escolha === 'padrao' ? p.padrao : v.escolha === 'conceder';
                const travada = proprio && p.codigo === 'usuarios.gerir';
                return (
                  <tr key={p.codigo} className={v.escolha !== 'padrao' ? 'com-ajuste' : ''}>
                    <td>{p.descricao}<small className="mono">{p.codigo}</small></td>
                    <td>{p.padrao ? <span className="sim"><Check size={15} aria-hidden /> sim</span> : <span className="nao"><Minus size={15} aria-hidden /> não</span>}</td>
                    <td>
                      {podeGerir && !travada ? (
                        <>
                          <select value={v.escolha} aria-label={`Ajuste: ${p.descricao}`} onChange={(e) => setM({ ...m, [p.codigo]: { ...v, escolha: e.target.value as Escolha } })}>
                            <option value="padrao">Seguir o papel</option>
                            {!p.padrao && <option value="conceder">Conceder</option>}
                            {p.padrao && <option value="retirar">Retirar</option>}
                          </select>
                          {v.escolha !== 'padrao' && <input className="motivo" maxLength={300} placeholder="Motivo (recomendado)" value={v.motivo} onChange={(e) => setM({ ...m, [p.codigo]: { ...v, motivo: e.target.value } })} />}
                        </>
                      ) : (v.escolha === 'padrao' ? 'Segue o papel' : v.escolha === 'conceder' ? 'Concedida' : 'Retirada')}
                      {p.ajuste && <small>{p.ajuste.concedida ? 'concedida' : 'retirada'} por {p.ajuste.criado_por ?? '—'} em {quando(p.ajuste.criado_em)}{p.ajuste.motivo ? ` — ${p.ajuste.motivo}` : ''}</small>}
                      {travada && <small>Ninguém tira de si a gestão de usuários.</small>}
                    </td>
                    <td>{fica ? <span className="situacao s-ok"><Plus size={13} aria-hidden /> tem</span> : <span className="situacao s-na"><X size={13} aria-hidden /> não tem</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          ))}
        </table>
      </div>
    </section>
  );
}

function Tecnico({ d, podeGerir, aoSalvar }: { d: Detalhe; podeGerir: boolean; aoSalvar: () => void }) {
  const [veiculos, setVeiculos] = useState<Veiculo[]>([]);
  const [vid, setVid] = useState(d.veiculo_id ?? '');
  const [esp, setEsp] = useState((d.especialidades ?? []).join(', '));
  const [erro, setErro] = useState<string | null>(null);
  useEffect(() => { api<Veiculo[]>('/veiculos').then(setVeiculos).catch(() => setVeiculos([])); }, []);
  const atual = veiculos.find((v) => v.id === d.veiculo_id);
  const mudou = vid !== (d.veiculo_id ?? '') || esp !== (d.especialidades ?? []).join(', ');
  async function salvar() {
    setErro(null);
    try {
      await api(`/usuarios/${d.id}/tecnico`, { metodo: 'PUT', corpo: { veiculo_id: vid || null, especialidades: esp.split(',').map((s) => s.trim()).filter(Boolean) } });
      aoSalvar();
    } catch (err) { setErro(err instanceof Error ? err.message : 'Não foi possível salvar.'); }
  }
  return (
    <section className="cartao ficha">
      <h2>Técnico</h2>
      {podeGerir ? (
        <div className="form form-compacto">
          <label>Carro habitual
            <select value={vid} onChange={(e) => setVid(e.target.value)}>
              <option value="">Sem carro habitual</option>
              {veiculos.filter((v) => v.ativo || v.id === d.veiculo_id).map((v) => <option key={v.id} value={v.id}>{placaFmt(v.placa)}{v.descricao ? ` — ${v.descricao}` : ''}</option>)}
            </select>
            <small className="ajuda">Peça lançada na OS sem carro sai do estoque deste carro; sem carro habitual, do almoxarifado.</small>
          </label>
          <label>Especialidades<input value={esp} onChange={(e) => setEsp(e.target.value)} placeholder="portao, cftv, cerca, acesso, rede" /></label>
          {erro && <p className="alerta" role="alert">{erro}</p>}
          {mudou && <div className="acoes-form"><button className="btn" onClick={salvar}>Salvar</button></div>}
        </div>
      ) : (
        <dl>
          <div><dt>Carro habitual</dt><dd className="mono">{atual ? placaFmt(atual.placa) : '—'}</dd></div>
          <div><dt>Especialidades</dt><dd>{(d.especialidades ?? []).join(', ') || '—'}</dd></div>
        </dl>
      )}
      {atual && !atual.centro_estoque && <p className="nota">Este carro ainda não tem centro no EstoqueMaza.</p>}
    </section>
  );
}

export function DetalheUsuario() {
  const { id } = useParams();
  const { usuario } = useSessao();
  const [d, setD] = useState<Detalhe | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [editar, setEditar] = useState(false);
  const [senha, setSenha] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const podeGerir = pode(usuario, 'usuarios.gerir');
  const podeVeiculos = pode(usuario, 'veiculos.gerir');
  const proprio = usuario?.id === id || usuario?.usuarioId === id;

  const carregar = useCallback(() => {
    api<Detalhe>(`/usuarios/${id}`).then(setD).catch((e) => setErro(e instanceof ErroApi && e.status === 404 ? 'Usuário não encontrado.' : e.message));
  }, [id]);
  useEffect(carregar, [carregar]);

  if (erro) return <><Link to="/equipe" className="voltar"><ArrowLeft size={16} /> Equipe</Link><p className="alerta">{erro}</p></>;
  if (!d) return <p className="nota">Carregando…</p>;

  async function acao(fn: () => Promise<unknown>, msg: string) {
    setAviso(null);
    try { await fn(); setAviso(msg); carregar(); } catch (e) { setAviso((e as Error).message); }
  }
  const novaSenha = () => {
    if (!window.confirm(`Gerar nova senha provisória para ${d.nome}? A senha atual deixa de valer e a pessoa sai de todos os aparelhos.`)) return;
    api<{ senha_provisoria: string }>(`/usuarios/${d.id}/senha-provisoria`, { metodo: 'POST' }).then((r) => { setSenha(r.senha_provisoria); carregar(); }).catch((e) => setAviso(e.message));
  };
  const alternarAtivo = () => {
    if (d.ativo && !window.confirm(`Desativar ${d.nome}? A pessoa sai na hora de todos os aparelhos e não consegue mais entrar. O histórico (OS, fotos) fica.`)) return;
    acao(() => api(`/usuarios/${d.id}`, { metodo: 'PATCH', corpo: { ativo: !d.ativo, versao: d.versao } }), d.ativo ? 'Usuário desativado.' : 'Usuário reativado.');
  };

  return (
    <>
      <Link to="/equipe" className="voltar"><ArrowLeft size={16} aria-hidden /> Equipe</Link>
      <div className="cabecalho-pagina">
        <div>
          <span className="kicker">{NOME_PAPEL[d.papel] ?? d.papel}</span>
          <h1 className="titulo">{d.nome}</h1>
          <div className="meta">
            {!d.ativo ? <span className="situacao s-na">Desativado</span> : d.trocar_senha ? <span className="situacao s-aviso">Senha provisória — ainda não trocou</span> : <span className="situacao s-ok">Ativo</span>}
            <span>{d.email}</span>
            {proprio && <span>(você)</span>}
          </div>
        </div>
        {podeGerir && (
          <div className="botoes">
            <button className="btn-fantasma" onClick={() => setEditar(true)}><Pencil size={16} aria-hidden /> Editar</button>
            {!proprio && <button className="btn-fantasma" onClick={novaSenha}><KeyRound size={16} aria-hidden /> Nova senha</button>}
            {!proprio && <button className={d.ativo ? 'btn-fantasma perigo' : 'btn-fantasma'} onClick={alternarAtivo}>{d.ativo ? 'Desativar' : 'Reativar'}</button>}
          </div>
        )}
      </div>
      {aviso && <p className="alerta alerta-ok" role="status">{aviso}</p>}

      <div className="grade-ficha">
        <section className="cartao ficha">
          <h2>Dados</h2>
          <dl>
            <div><dt>Telefone</dt><dd>{telefoneFmt(d.telefone)}</dd></div>
            <div><dt>Último acesso</dt><dd>{quando(d.ultimo_login_em)}</dd></div>
            <div><dt>Cadastrado em</dt><dd>{quando(d.criado_em)}</dd></div>
            <div><dt>Exceções de permissão</dt><dd>{d.permissoes.filter((p) => p.ajuste).length}</dd></div>
          </dl>
          {podeGerir && (
            <>
              <h3 className="subtitulo">Aparelhos conectados</h3>
              {d.sessoes.length === 0 ? <p className="nota">Nenhum.</p> : (
                <ul className="lista-simples">
                  {d.sessoes.map((s) => <li key={s.id}><strong>{s.dispositivo ?? 'Aparelho'}</strong><small>entrou {quando(s.criado_em)} · último uso {quando(s.ultimo_uso_em)}</small></li>)}
                </ul>
              )}
              {d.sessoes.length > 0 && !proprio && (
                <button className="btn-fantasma" onClick={() => window.confirm('Desconectar todos os aparelhos desta pessoa? (celular perdido, por exemplo)') && acao(() => api(`/usuarios/${d.id}/encerrar-sessoes`, { metodo: 'POST' }), 'Aparelhos desconectados.')}>
                  <LogOut size={15} aria-hidden /> Desconectar todos
                </button>
              )}
            </>
          )}
        </section>
        {d.papel === 'tecnico' && <Tecnico d={d} podeGerir={podeVeiculos} aoSalvar={carregar} />}
      </div>

      <Permissoes d={d} podeGerir={podeGerir} proprio={proprio} aoSalvar={carregar} />

      <Modal titulo="Editar usuário" aberto={editar} aoFechar={() => setEditar(false)}>
        <FormDados d={d} proprio={proprio} aoCancelar={() => setEditar(false)} aoSalvar={() => { setEditar(false); carregar(); }} />
      </Modal>
      <Modal titulo="Nova senha provisória" aberto={Boolean(senha)} aoFechar={() => setSenha(null)}>
        {senha && <SenhaProvisoria nome={d.nome} senha={senha} aoFechar={() => setSenha(null)} />}
      </Modal>
    </>
  );
}
